/**
 * @license Copyright 2019 Google Inc. All Rights Reserved.
 * Licensed under the Apache License, Version 2.0 (the "License"); you may not use this file except in compliance with the License. You may obtain a copy of the License at http://www.apache.org/licenses/LICENSE-2.0
 * Unless required by applicable law or agreed to in writing, software distributed under the License is distributed on an "AS IS" BASIS, WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied. See the License for the specific language governing permissions and limitations under the License.
 */

.audit-group {
  margin: calc(2 * var(--base-spacing));
}

.audit-group__header {
  display: flex;
  flex-direction: row;
}

.audit-group__title {
  font-size: var(--header-font-size);
  margin-bottom: calc(var(--base-spacing) / 2);
  flex-grow: 1;
}

.audit-group__big-picture-tabs {
  width: 380px;
  display: flex;
  flex-direction: row;
  margin-right: calc(var(--base-spacing) / 2);
}

.big-picture-tabs__tab {
  flex-grow: 1;

  cursor: pointer;

  font-size: var(--smaller-font-size);
  text-align: center;
  text-transform: uppercase;
  color: var(--secondary-text-color);
  font-weight: var(--medium-font-weight);

  border-bottom: 1px solid var(--base-border-color);
  margin-bottom: calc(var(--base-spacing) / 2);
}

.big-picture-tabs__tab--selected {
  color: var(--base-text-color);
  border-bottom: 1px solid var(--strong-border-color);
}

.audit-group__audit {
  display: flex;
  flex-direction: row;
  align-items: center;

  height: var(--audit-group-audit-height);
  margin: 0 calc(var(--base-spacing) * -1);
  padding: 0 var(--base-spacing);
}

.audit-group__audit:hover {
  cursor: pointer;
  background: var(--secondary-background-color);
}

.audit-group__expand-toggle {
  display: none;
  cursor: pointer;
}

.audit-group__expand-toggle i {
  transform: rotate(90deg);
}

.audit-group__audit-title {
  margin-left: var(--base-spacing);
  font-weight: var(--medium-font-weight);
  min-width: 25px;
  max-width: 70%;

  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.audit-group__audit-diff {
  flex-grow: 1;

  display: flex;
  flex-direction: row;
  justify-content: flex-end;
  align-items: center;
  font-family: var(--monospace-font-family);
  font-size: var(--monospace-font-size);
}

.audit-group__error-message {
  display: flex;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.audit-group__error-message i {
  margin-right: calc(var(--base-spacing) / 2);
}

.audit-group__diff-arrow {
  color: var(--secondary-text-color);
  font-size: var(--arrow-icon-size);
  line-height: var(--large-icon-size);
  margin: 0 var(--base-spacing);
}

.audit-group__diff-arrow--regression {
  color: var(--regression-color);
}

.audit-group__diff-arrow--improvement {
  color: var(--improvement-color);
}

.audit-group__diff-badge-group {
  --badge-height: 16px;

  position: relative;
  height: var(--large-icon-size);
}

.audit-group__diff-badge-group:last-child {
  margin-right: 20px;
}

.audit-group__diff-badges {
  position: absolute;
  top: calc(-1 * var(--badge-height) / 5);
  left: calc(100% - var(--badge-height) * 4 / 5);

  height: var(--badge-height);

  display: flex;
  flex-direction: row;
}

.audit-group__diff-badge {
  display: inline-block;

  height: var(--badge-height);
  min-width: var(--badge-height);
  border-radius: calc(var(--badge-height) * 2);

  padding: 0 calc(var(--badge-height) / 4);
  margin-right: calc(-1 * var(--badge-height) / 4);

  line-height: var(--badge-height);
  font-size: var(--subtext-font-size);
  text-align: center;

  background: var(--neutral-highlight-color);
  color: var(--secondary-text-color);
}

.audit-group__diff-badge-group--multiple .audit-group__diff-badge:first-child {
  border-top-right-radius: 0;
  border-bottom-right-radius: 0;
  margin-right: 0;
}

.audit-group__diff-badge-group--multiple .audit-group__diff-badge:last-child {
  border-top-left-radius: 0;
  border-bottom-left-radius: 0;
}

.audit-group__diff-badge--regression {
  background: var(--regression-color);
  color: var(--inverted-text-color);

  z-index: 1;
}

.audit-group__diff-badge--improvement {
  background: var(--improvement-color);
  color: var(--inverted-text-color);
}

.audit-group--unchanged .audit-group__header {
  cursor: pointer;
}

.audit-group--unchanged .audit-group__audit-diff {
  display: none;
}

.audit-group--unchanged .audit-group__audit:hover .audit-group__audit-diff {
  display: flex;
}

.audit-group--unchanged .audit-group__expand-toggle {
  display: block;
}

.audit-group--expanded .audit-group__expand-toggle i {
  transform: rotate(-90deg);
}
